// Light/dark theme selection, persisted per device.
//
// Three modes, not two: SYSTEM follows the operating system's own setting and
// is the default, so someone whose laptop is already dark gets a dark BowVue
// without ever opening a menu. LIGHT and DARK are explicit overrides.
//
// Persistence mirrors plugins/i18n.js setLocale: localStorage, per device,
// guarded because Safari private browsing throws on access rather than
// returning null. The value stored is the MODE ('system'), never the resolved
// theme ('dark'), or a machine whose OS setting later changes would be stuck on
// whatever it happened to be the day the user chose.
export const THEME_MODES = ['system', 'light', 'dark']
export const STORAGE_KEY = 'bowvue.theme'

const DARK_QUERY = '(prefers-color-scheme: dark)'

// A module-level handle to the running Vuetify instance. Set once at startup by
// installTheme; the toggle calls setThemeMode rather than reaching into Vuetify
// itself, so persistence, the OS listener and the applied theme stay in step.
let vuetifyTheme = null
let mediaQuery = null

export function storedThemeMode() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    return THEME_MODES.includes(saved) ? saved : 'system'
  } catch (e) {
    // Private browsing and blocked site data both throw here.
    return 'system'
  }
}

// prefersDark reads the OS setting. Returns false wherever the API is missing
// (jsdom, older browsers, SSR), which lands on light rather than throwing.
export function prefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  try {
    return window.matchMedia(DARK_QUERY).matches
  } catch (e) {
    return false
  }
}

// resolveTheme turns a MODE into the theme name Vuetify should apply.
export function resolveTheme(mode) {
  if (mode === 'dark') return 'dark'
  if (mode === 'light') return 'light'
  return prefersDark() ? 'dark' : 'light'
}

function applyResolved(mode) {
  const name = resolveTheme(mode)
  if (vuetifyTheme) {
    // Vuetify 3.3+ exposes `global.name`; older builds used `name` directly.
    const target = vuetifyTheme.global ? vuetifyTheme.global.name : vuetifyTheme
    if (target && 'value' in target) target.value = name
  }
  syncHtmlTheme(name)
  return name
}

// setThemeMode switches the theme, persists the choice, and keeps the
// <html data-theme> attribute in step. Exported for the theme toggle; call this
// rather than setting Vuetify's theme directly.
export function setThemeMode(mode) {
  if (!THEME_MODES.includes(mode)) return storedThemeMode()
  try {
    localStorage.setItem(STORAGE_KEY, mode)
  } catch (e) {
    /* ignore persistence failure; the choice still applies for this session */
  }
  applyResolved(mode)
  return mode
}

// installTheme wires the Vuetify instance up at startup: applies whatever was
// stored, then follows the OS while the mode is 'system'.
export function installTheme(vuetify) {
  vuetifyTheme = vuetify && vuetify.theme ? vuetify.theme : null
  applyResolved(storedThemeMode())
  listenToSystem()
}

// The OS can change under a running tab (a scheduled dark mode, or the user
// flipping it). Only re-apply while the mode is 'system'. An explicit choice
// must not be overridden by the machine.
function listenToSystem() {
  if (typeof window === 'undefined' || !window.matchMedia) return
  try {
    mediaQuery = window.matchMedia(DARK_QUERY)
  } catch (e) {
    return
  }
  const onChange = () => {
    if (storedThemeMode() === 'system') applyResolved('system')
  }
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', onChange)
  } else if (mediaQuery.addListener) {
    // Safari < 14 has no addEventListener on MediaQueryList.
    mediaQuery.addListener(onChange)
  }
}

// syncHtmlTheme mirrors syncHtmlLang: it puts the resolved theme on <html> so
// plain CSS (the marketing pages' own styles, the scrollbar, anything outside
// a Vuetify component) can key off it without importing anything.
function syncHtmlTheme(name) {
  if (typeof document === 'undefined' || !document.documentElement) return
  document.documentElement.setAttribute('data-theme', name)
  document.documentElement.style.colorScheme = name
}
